import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PostAddIcon from "@mui/icons-material/PostAdd";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Box, IconButton, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { OpenShiftCard } from "../../../components/OpenShiftCard/OpenShiftCard";
import { StatusBadge } from "../../../components/OpenShiftCard/StatusBadge";
import { statusStyles } from "../../../components/OpenShiftCard/utils";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import { ThemedSvg } from "../../../components/UIToolkit/ThemedSvg";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useDepartmentColor } from "../../../hooks/useDepartmentColor";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useCancelReplaceShiftRequest, useCreateReplaceShiftRequest } from "../../../hooks/useReplaceShiftRequest";
import { trackEvent } from "../../../utils/analytics";
import { formatHoursMinutes, formatPtoTimeRange, getShiftDisplayDate } from "../../../utils/dateUtils";
import { mergeGroupKey } from "../../../utils/incentive";
import {
	NotificationType,
	NurseFullRole,
	NurseFullRoleShort,
	type NurseInShift,
	type NurseRole,
	type OpenShiftRequest,
	type OpenShiftType,
	type ReplaceShiftRequest,
	RequestResponsesStatusOptions,
	RequestStatus,
	ShiftType,
	type TransformedShift,
} from "../../../utils/models";
import { ShiftTypeIcon } from "../../MasterSchedule/v2/auxillary/ShiftTypeIcon";
import CallOffDrawer from "./CallOffDrawer";
import SwapDrawer from "./SwapDrawer";
import SwapRequestDrawer from "./SwapRequestDrawer";

// Width of the leading shift-type icon column and the gap between it and the content.
const SHIFT_ICON_SIZE_PX = 24 as const satisfies number;
const SHIFT_ROW_GAP = 2 as const satisfies number; //MUI sx gap value, not pixel value
const STATUS_BADGE_INDENT_PX: number = SHIFT_ICON_SIZE_PX + SHIFT_ROW_GAP * 8; // mui numeric gap * 8px

interface ShiftCardProps {
	dateKey: string;
	currentDate: DateTime;
	shifts: TransformedShift[];
	isSelected?: boolean;
	isExpanded?: boolean;
	onItemClick: (shift: TransformedShift) => void;
	weekShifts?: Array<TransformedShift>;
	onCancelRequest?: () => void;
	variant?: "simple" | "full";
	hideDate?: boolean;
	readOnly?: boolean;
}

export const ShiftCard: FC<ShiftCardProps> = ({
	dateKey,
	currentDate,
	shifts,
	isSelected = false,
	isExpanded = false,
	onItemClick,
	weekShifts = [],
	onCancelRequest,
	variant = "full",
	hideDate = false,
	readOnly = false,
}) => {
	const { mergedRequests, refreshRequests, myRequests, staffRequests } = useRequestsContext();
	const isNotesEnabled = useFeatureFlag(FEATURE_FLAGS.BedSideNotes);
	const formatPersonName = useFormatPersonName();
	const shouldSurfaceDisciplinaryCallOffImpact = useFeatureFlag(FEATURE_FLAGS.SurfaceDisciplinaryCallOffImpact);

	const [expandedShiftIds, setExpandedShiftIds] = useState<string[]>([]);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedShiftForSwap, setSelectedShiftForSwap] = useState<TransformedShift | null>(null);
	const [selectedShiftForCallOff, setSelectedShiftForCallOff] = useState<TransformedShift | null>(null);
	// Held separately from selectedShiftForCallOff so the drawer only opens once the impact warning is acknowledged.
	const [shiftPendingCallOffConfirmation, setShiftPendingCallOffConfirmation] = useState<TransformedShift | null>(null);
	const [selectedSwapRequest, setSelectedSwapRequest] = useState<TransformedShift | null>(null);
	const { hospitalData, chosenDepartment, timezone, showErrorSnackbar, getDepartmentDisplayName, getWeekIndexByDateTime } = useHospitalData();
	const departmentColor = useDepartmentColor();
	const { userInfo } = useAuth();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();

	const dateOpenShifts: OpenShiftType[] = useMemo(
		() =>
			mergedRequests.filter(
				(r) => getShiftDisplayDate(r.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).toFormat("yyyy-MM-dd") === dateKey,
			),
		[mergedRequests, dateKey, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const { mutateAsync: createReplaceShiftRequest, isPending: isCreatingRequest } = useCreateReplaceShiftRequest();
	const { mutateAsync: cancelReplaceShiftRequest, isPending: isCancelingRequest } = useCancelReplaceShiftRequest();

	const isLoading = isCreatingRequest || isCancelingRequest;
	const nurse = useMemo(() => hospitalData.nurses.find((n) => n.userId === userInfo.id), [hospitalData, userInfo]);

	const selectedWeekIndex = useMemo(() => {
		if (!selectedShiftForSwap?.startTime) {
			return 0;
		}

		return getWeekIndexByDateTime(selectedShiftForSwap.startTime);
	}, [selectedShiftForSwap, getWeekIndexByDateTime]);

	const { t } = useTranslation();

	const getNurseStatus = (openShift: OpenShiftType) => {
		return openShift.myStatus || "";
	};

	const getShiftStatus = (openShift: OpenShiftType) => {
		return openShift.status || "";
	};

	const hasOpenShifts =
		dateOpenShifts.filter(
			(shift) => getNurseStatus(shift) !== RequestResponsesStatusOptions.APPROVED && getShiftStatus(shift) !== RequestResponsesStatusOptions.CLOSED,
		).length > 0;
	const hasDayOpenShifts = dateOpenShifts.some(
		(shift) =>
			shift.type === ShiftType.Day &&
			getNurseStatus(shift) !== RequestResponsesStatusOptions.APPROVED &&
			getShiftStatus(shift) !== RequestResponsesStatusOptions.CLOSED,
	);
	const hasNightOpenShifts = dateOpenShifts.some(
		(shift) =>
			shift.type === ShiftType.Night &&
			getNurseStatus(shift) !== RequestResponsesStatusOptions.APPROVED &&
			getShiftStatus(shift) !== RequestResponsesStatusOptions.CLOSED,
	);

	const handleExpandShift = (shiftId: string) => {
		setExpandedShiftIds((prev) => (prev.includes(shiftId) ? prev.filter((id) => id !== shiftId) : [...prev, shiftId]));

		trackEvent("shift_card_expanded", {
			shift_id: shiftId,
			is_expanding: !expandedShiftIds.includes(shiftId),
			date: dateKey,
		});
	};

	const handleMenuClick = (event: React.MouseEvent<HTMLElement>, shiftType: string) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);

		trackEvent("shift_card_menu_opened", {
			date: dateKey,
			shift_type: shiftType || "unknown",
		});
	};

	const handleMenuClose = (event: React.MouseEvent<HTMLElement>, shiftType: string) => {
		event.stopPropagation();
		setAnchorEl(null);

		trackEvent("shift_card_menu_closed", {
			date: dateKey,
			shift_type: shiftType || "unknown",
		});
	};

	const handleCancelReplaceRequest = async (event: React.MouseEvent<HTMLElement>, shiftType: string) => {
		if (variant !== "full") {
			showSnackbar(t("error.shift.not.full"), "error");
			return;
		}

		event.stopPropagation();
		setAnchorEl(null);
		const user_id = String(userInfo.id);
		const requestToCancel: ReplaceShiftRequest | OpenShiftRequest | undefined = myDailyRequests.find(
			(request) =>
				String(request.created_by) === user_id &&
				(request.request_type === NotificationType.ReplaceShift || request.request_type === NotificationType.Swap) &&
				request.message.shift_type === (shiftType === ShiftType.Night ? ShiftType.Night : ShiftType.Day) &&
				request.message.shift_start.toFormat("yyyy-MM-dd") === dateKey,
		);

		if (!requestToCancel) {
			showSnackbar(t("error.request.not.found"), "error");
			return;
		}

		try {
			trackEvent("Shift Card", {
				action: "cancel open shift request",
				shift_start: requestToCancel.message.shift_start,
				shift_end: requestToCancel.message.shift_end,
				shift_type: requestToCancel.message.shift_type,
				open_shift_id: requestToCancel.id,
			});

			await cancelReplaceShiftRequest({
				requestId: requestToCancel.id,
				departmentId: requestToCancel.department_id,
			});

			trackEvent("Shift Card", {
				action: "cancel replace shift request success",
				shift_start: requestToCancel.message.shift_start,
				shift_end: requestToCancel.message.shift_end,
				shift_type: requestToCancel.message.shift_type,
				open_shift_id: requestToCancel.id,
			});
			showSnackbar(t("success.replace.request.cancelled"), "success");
			await refreshRequests();
		} catch (error: unknown) {
			showErrorSnackbar(
				"Failed to cancel replace request. Please try again.",
				{
					action: "cancel request shift replacement failed",
					shift_start: requestToCancel.message.shift_start,
					shift_end: requestToCancel.message.shift_end,
					shift_type: requestToCancel.message.shift_type,
					open_shift_id: requestToCancel.id,
				},
				error,
			);
		}
	};

	const handleReplaceShift = async (event: React.MouseEvent<HTMLElement>, shift: TransformedShift) => {
		if (variant !== "full") {
			showSnackbar(t("error.shift.not.full"), "error");
			return;
		}

		event.stopPropagation();
		setAnchorEl(null);
		const shiftNo = shift.shiftType === ShiftType.Night ? 1 : 0;

		if (!nurse) {
			showSnackbar(t("error.nurse.not.found"), "error");
			return;
		}

		const { role } = nurse;

		const shiftDate = DateTime.fromISO(dateKey).setZone(timezone);
		const today = DateTime.now().setZone(timezone);
		const dayNumber = shiftDate.diff(today, "day").days;

		const shortRole = NurseFullRoleShort[role] as unknown as NurseRole;

		let roles = Array.from(
			new Set(
				Object.values(chosenDepartment.role_groups)
					.filter((groupRoles) => groupRoles.includes(shortRole))
					.flatMap((groupRoles) => groupRoles),
			),
		)
			.map((fullRole) => NurseFullRole[fullRole as NurseRole])
			.filter(Boolean);

		if (!roles?.length) {
			roles = [role];
		}

		try {
			trackEvent("Shift Card", {
				action: "request swap",
				shift_start: shift.startTime?.toISO() as string,
				shift_end: shift.endTime?.toISO() as string,
				shift_type: shiftNo === 0 ? ShiftType.Day : ShiftType.Night,
				roles: roles,
				created_by: String(userInfo.id),
			});

			await createReplaceShiftRequest({
				departmentId: shift.departmentId || "",
				dayNumber,
				shiftType: shiftNo === 0 ? ShiftType.Day : ShiftType.Night,
				roles,
				createdBy: String(userInfo.id),
				shiftStart: shift.startTime?.toISO() as string,
				shiftEnd: shift.endTime?.toISO() as string,
				replacementNurseId: nurse.id,
				location: shift.location,
				replacementShiftId: shift.id,
				chargeOnly: nurse.isShiftManager || nurse.canBeReliefCharge || false,
				bonus: shift.bonus,
				jobId: shift.jobId,
			});

			showSnackbar(t("snakebar.shift.replace.success"), "success");
			trackEvent("Shift Card", {
				action: "request swap success",
				shift_start: shift?.startTime?.toISO(),
				shift_end: shift?.endTime?.toISO(),
				shift_type: shiftNo === 0 ? ShiftType.Day : ShiftType.Night,
			});
			await refreshRequests();
		} catch (error: unknown) {
			showErrorSnackbar(
				"Failed to create replace shift request. Please try again.",
				{
					action: "request swap failed",
					shift_start: shift?.startTime?.toISO(),
					shift_end: shift?.endTime?.toISO(),
					shift_type: shiftNo === 0 ? ShiftType.Day : ShiftType.Night,
				},
				error,
			);
		}
	};

	const handleSwapShift = (event: React.MouseEvent<HTMLElement>, shift: TransformedShift) => {
		event.stopPropagation();
		setAnchorEl(null);
		if (!shift.id) {
			showSnackbar(t("error.shift.not.found"), "error");
			return;
		}
		setSelectedShiftForSwap(shift);
	};

	const callOffImpactEventProps = (shift: TransformedShift | null) => ({
		date: dateKey,
		shift_id: shift?.id,
		department_id: shift?.departmentId,
		shift_type: shift?.shiftType,
		shift_start: shift?.startTime?.toISO(),
	});

	const handleCallOffShift = (event: React.MouseEvent<HTMLElement>, shift: TransformedShift) => {
		event.stopPropagation();
		setAnchorEl(null);
		if (shouldSurfaceDisciplinaryCallOffImpact) {
			setShiftPendingCallOffConfirmation(shift);
			track("call_off_disciplinary_impact_warning_shown", callOffImpactEventProps(shift));
			return;
		}
		setSelectedShiftForCallOff(shift);
	};

	const handleConfirmCallOffImpact = () => {
		track("call_off_disciplinary_impact_warning_confirmed", callOffImpactEventProps(shiftPendingCallOffConfirmation));
		setSelectedShiftForCallOff(shiftPendingCallOffConfirmation);
		setShiftPendingCallOffConfirmation(null);
	};

	const handleCancelCallOffImpact = () => {
		track("call_off_disciplinary_impact_warning_cancelled", callOffImpactEventProps(shiftPendingCallOffConfirmation));
		setShiftPendingCallOffConfirmation(null);
	};

	const userCreatedOpenShift = useMemo(
		() =>
			myRequests.find(
				(req) =>
					req.message.date.toFormat("yyyy-MM-dd") === dateKey &&
					shifts?.find((shift) => req.message.shift_type === (shift?.shiftType === ShiftType.Night ? ShiftType.Night : ShiftType.Day)),
			),
		[myRequests, dateKey, shifts],
	);

	const myDailyRequests = useMemo(() => {
		return myRequests.filter((req) => req.message.shift_start.toFormat("yyyy-MM-dd") === dateKey);
	}, [myRequests, dateKey]);

	const activeRequests = useMemo(() => {
		const activeRequest = myDailyRequests.find((r) => r.status === RequestStatus.Active);

		if (!activeRequest) return;

		return activeRequest.request_type === NotificationType.Swap ? NotificationType.Swap : NotificationType.ReplaceShift;
	}, [myDailyRequests]);

	const dayRequests = useMemo(() => {
		return mergedRequests.filter(
			(req) =>
				getShiftDisplayDate(req.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).toFormat("yyyy-MM-dd") === dateKey &&
				req.status !== RequestResponsesStatusOptions.APPROVED,
		);
	}, [mergedRequests, dateKey, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes]);

	const hasShiftSwapRequest = useCallback(
		(shiftId?: string) => {
			if (!shiftId || staffRequests.length === 0) return false;

			return staffRequests.find(
				(r) =>
					r.request_type === NotificationType.Swap &&
					(r as ReplaceShiftRequest).request_target_shift === shiftId &&
					r.status === RequestStatus.Active &&
					r.message.request_data.length === 0,
			);
		},
		[staffRequests],
	);

	// Calculate number of unique requests (after merging)
	const nurseNumberOfRequests = useMemo(() => {
		return dayRequests.filter((req) => req.status === RequestResponsesStatusOptions.PENDING || req.status === "").length;
	}, [dayRequests]);

	const showBonusIcon = useMemo<boolean>(
		() => dayRequests.some((r) => (r.current_bonus ?? r.bonus) && (r.status === RequestResponsesStatusOptions.PENDING || r.status === "")),
		[dayRequests],
	);

	const swapDrawerData = useMemo(() => {
		if (!selectedShiftForSwap?.startTime || !nurse || !selectedShiftForSwap.id) {
			return null;
		}

		const nurseInShift: NurseInShift = {
			id: selectedShiftForSwap.id,
			nurseId: nurse.id,
			departmentId: selectedShiftForSwap.departmentId,
			startTime: selectedShiftForSwap.startTime,
			endTime: selectedShiftForSwap.endTime || selectedShiftForSwap.startTime.plus({ hours: 12 }),
			isPublished: true,
			isPending: false,
			isPendingToDelete: false,
			location: selectedShiftForSwap.location,
			role: nurse.role,
			charge: !!selectedShiftForSwap.isCharge,
			name: formatPersonName(nurse),
			type: nurse.employmentType,
			shiftType: selectedShiftForSwap.shiftType || ShiftType.Day,
			bonus: selectedShiftForSwap.bonus || 0,
			notes: "",
			breaks: [],
			trainingStatus: nurse.trainingStatus,
		};

		const dayIndex = currentDate.weekday % 7;

		return {
			nurse: nurseInShift,
			weekIndex: 0,
			dayIndex,
		};
	}, [selectedShiftForSwap, nurse, currentDate, formatPersonName]);

	const groupRequests = (requests: OpenShiftType[]) => {
		return requests.reduce((groups: Record<string, OpenShiftType[]>, request) => {
			const key = `${request.startTime}-${request.endTime}-${request.type}-${mergeGroupKey(request.id, request.incentive_windows)}-${request.status}`;
			if (!groups[key]) {
				groups[key] = [];
			}
			groups[key].push(request);
			return groups;
		}, {});
	};

	const dateStatus = (dayRequests: OpenShiftType[], shift: TransformedShift) => {
		const swapAprooved = staffRequests.some(
			(r) =>
				r.request_type === NotificationType.Swap &&
				r.status === RequestStatus.Active &&
				r.message.request_data.length > 0 &&
				(r as ReplaceShiftRequest).request_target_shift === shift.id,
		);

		// APP-29: a confirmed scheduled shift is a real NurseInShift, distinguished by its `id`. Its badge must reflect
		// THIS shift's own state (e.g. an approved swap targeting it), never the day's open-shift pickup requests — a
		// pending pickup for a DIFFERENT open shift on the same day used to bleed its "pending" badge onto the confirmed
		// card. The pickup keeps its own pending presentation in the expanded open-shifts section below. Day-level pickup
		// statuses still drive the badge on non-shift rows (PTO / empty-day placeholders, which carry no `id`).
		const isConfirmedScheduledShift = !!shift.id;
		const pickupRequestsForBadge = isConfirmedScheduledShift ? [] : dayRequests;
		const groupedRequests = groupRequests(pickupRequestsForBadge);

		// For a confirmed scheduled shift `pickupRequestsForBadge` is empty, so the grouped-request branches below are
		// inert and only `swapAprooved` can drive its badge. They remain active for non-shift rows.
		// First check for pending, then other statuses
		const hasPending = Object.values(groupedRequests).some((group) => group.some((r) => r.status === RequestResponsesStatusOptions.PENDING));

		const hasApproved = Object.values(groupedRequests).some((group) => group.some((r) => r.status === RequestResponsesStatusOptions.APPROVED));

		const hasDeclined = Object.values(groupedRequests).some((group) => group.some((r) => r.status === RequestResponsesStatusOptions.DECLINED));
		const hasClosed = Object.values(groupedRequests).some((group) => group.some((r) => r.status === RequestResponsesStatusOptions.CLOSED));
		// show only one status- if there is a pending, show pending, if there is an approved, show approved, if there is a declined or closed, show declined or closed

		if (hasPending || swapAprooved) {
			return <StatusBadge status={RequestResponsesStatusOptions.PENDING.toLowerCase()} />;
		}
		if (hasApproved) {
			return <StatusBadge status={RequestResponsesStatusOptions.APPROVED.toLowerCase()} />;
		}
		if (hasDeclined || hasClosed) {
			return <StatusBadge status={RequestResponsesStatusOptions.CLOSED.toLowerCase()} />;
		}

		return null;
	};

	const isSwapEnabled = useFeatureFlag(FEATURE_FLAGS.EnableSwap);
	const isBedSideCallOffEnabled = useFeatureFlag(FEATURE_FLAGS.BedSideCallOff);
	const shouldDisplayLocation = useFeatureFlag(FEATURE_FLAGS.ShouldDisplayLocationInStaffApp);
	const isReplacementRequestEnabled = useFeatureFlag(FEATURE_FLAGS.ReplacementRequestEnabled);

	return (
		<Box
			data-testid={`shift-card-${dateKey}`}
			sx={{
				mb: variant === "simple" ? 2 : 1,
				borderRadius: "8px",
				overflow: "hidden",
				...(isSelected && {
					borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
				}),
				position: "relative",
				zIndex: 0,
			}}
		>
			{shifts?.map((shift, index) => (
				<Paper
					id={`shift-${dateKey}`}
					data-testid={`my-shift-${dateKey}`}
					key={`shift-${dateKey}-${index}`}
					elevation={0}
					onClick={() => onItemClick(shift)}
					sx={{
						py: variant === "simple" ? 2 : 1.5,
						px: 2,
						borderRadius: (hasOpenShifts || (shift?.requests ?? 0) > 0) && isExpanded ? "8px 8px 0 0" : "8px",
						cursor: "pointer",
						transition: "all 0.2s ease-in-out",
						"&:hover": {
							bgcolor: "#F8F9FB",
						},
						...(variant === "simple" && {
							backgroundColor: "white",
						}),
						position: "relative",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "flex-start", gap: SHIFT_ROW_GAP }}>
						<Box
							sx={{
								width: `${SHIFT_ICON_SIZE_PX}px`,
								height: `${SHIFT_ICON_SIZE_PX}px`,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							{shift?.isTimeOff && !shift?.shiftType ? (
								<ThemedSvg src="/day-off.svg" alt="Day off" size={17} color="var(--inhouse-primary)" />
							) : (
								shift?.shiftType && <ShiftTypeIcon shiftType={shift.shiftType} />
							)}
						</Box>

						<Box sx={{ flex: 1 }}>
							<Typography
								variant="subtitle2"
								sx={{
									color: shift?.shiftType ? "var(--inhouse-primary-dark)" : "primary.main",
									fontSize: "0.875rem",
									fontWeight: 600,
									mb: 0.5,
									display: "flex",
									alignItems: "center",
									gap: 1,
									...(hideDate && {
										display: "none",
									}),
								}}
							>
								{currentDate.toFormat("ccc, MMM d")}
							</Typography>

							{shift?.timeOffWindow && (
								<Typography
									variant="body2"
									data-testid={`my-shift-time-off-window-${dateKey}`}
									sx={{
										color: (theme) => alpha(theme.palette.primary.dark, 0.5),
										fontWeight: 600,
									}}
								>
									{`${formatPtoTimeRange(shift.timeOffWindow.start, shift.timeOffWindow.end)} · ${formatHoursMinutes(shift.timeOffWindow.hours)}`}
								</Typography>
							)}

							{shift?.startTime && (
								<Box>
									<Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
										<Box
											sx={{
												bgcolor: departmentColor(shift.departmentId).dot,
												width: "4px",
												height: shift.jobName && (nurse?.jobs?.length ?? 0) > 1 ? "60px" : "40px",
												borderRadius: "2px",
												mr: 1,
											}}
										/>
										<Box>
											<Typography
												variant="body2"
												sx={{
													color: (theme) => alpha(theme.palette.primary.dark, 0.5),
													fontSize: "0.875rem",
													fontWeight: 600,
												}}
											>
												{getDepartmentDisplayName(shift.departmentId)}
											</Typography>
											{shift.jobName && (nurse?.jobs?.length ?? 0) > 1 && (
												<Typography
													variant="body2"
													sx={{
														color: (theme) => alpha(theme.palette.primary.dark, 0.5),
														fontSize: "0.875rem",
														fontWeight: 600,
													}}
												>
													{shift.jobName}
												</Typography>
											)}
											<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
												<Typography
													variant="body2"
													sx={{
														color: (theme) => alpha(theme.palette.primary.dark, 0.5),
														fontSize: "0.875rem",
														fontWeight: 600,
													}}
												>
													{shift.startTime.toFormat("h:mm a")} - {shift.endTime?.toFormat("h:mm a") || ""}
												</Typography>
												{hasShiftSwapRequest(shift.id) && (
													<Box
														sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.5, cursor: "pointer" }}
														onClick={(e) => {
															e.stopPropagation();
															setSelectedSwapRequest(shift);
														}}
													>
														<SwapHorizIcon sx={{ fontSize: 14, color: "primary.main" }} />
														<Typography sx={{ fontSize: "0.75rem", color: "primary.main" }}>{t("has.active.swap.request.click")}</Typography>
													</Box>
												)}
											</Box>
										</Box>
									</Box>
								</Box>
							)}

							{shouldDisplayLocation && shift?.location && (
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									<Typography
										variant="body2"
										sx={{
											color: (theme) => alpha(theme.palette.primary.dark, 0.5),
											fontSize: "0.775rem",
										}}
									>
										{shift.location}
										{shift.isCharge && `, ${t("charge")}`}
									</Typography>
								</Box>
							)}
							{shift?.notes && isNotesEnabled && (
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
									<PostAddIcon sx={{ fontSize: "0.875rem", color: "primary.main" }} />
									<Typography
										variant="body2"
										sx={{
											color: (theme) => alpha(theme.palette.primary.dark, 0.5),
											fontSize: "0.775rem",
										}}
									>
										{shift.notes}
									</Typography>
								</Box>
							)}
							{activeRequests && (
								<Typography
									variant="body2"
									sx={{
										...statusStyles.base,
										...statusStyles.pending,
									}}
								>
									{activeRequests === NotificationType.Swap ? t("has.active.swap.request") : t("has.active.replace.request")}
								</Typography>
							)}
						</Box>

						{/* Right side indicators - Only show in full variant */}
						{variant === "full" && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
								{shift?.isTimeOff && (
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
											bgcolor: "#F1F3F9",
											px: 1.5,
											py: 0.5,
											borderRadius: "4px",
											zIndex: 1,
										}}
									>
										<Typography
											variant="body2"
											sx={{
												color: "primary.main",
												fontSize: "0.75rem",
												fontWeight: 600,
											}}
										>
											{shift.timeOffWindow ? t("time.off") : t("day.off")}
										</Typography>
									</Box>
								)}

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										ml: "auto",
										position: "relative",
									}}
								>
									{nurseNumberOfRequests > 0 && (shift?.hasDayRequest || hasDayOpenShifts) && (
										<Box
											sx={{
												width: 8,
												height: 8,
												borderRadius: "50%",
												border: "1.5px solid #FFBF00",
												bgcolor: "transparent",
											}}
										/>
									)}
									{nurseNumberOfRequests > 0 && (shift?.hasNightRequest || hasNightOpenShifts) && (
										<Box
											sx={{
												width: 8,
												height: 8,
												borderRadius: "50%",
												border: "1.5px solid #5F5CE8",
												bgcolor: "transparent",
											}}
										/>
									)}
									{((shift?.requests ?? 0) > 0 || hasOpenShifts) && (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.5,
												cursor: "pointer",
												userSelect: "none",
												"&:hover": {
													opacity: 0.8,
												},
											}}
										>
											{nurseNumberOfRequests > 0 && (
												<Typography
													variant="caption"
													sx={{
														color: "primary.main",
														fontSize: "0.75rem",
														fontWeight: 600,
														cursor: "pointer",
													}}
												>
													{nurseNumberOfRequests} {t("requests")}
												</Typography>
											)}
											{showBonusIcon && <img src="/bonus.svg" alt="Bonus" />}

											<KeyboardArrowDownIcon
												sx={{
													fontSize: 16,
													color: "primary.main",
													transition: "transform 0.2s ease",
													transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
													cursor: "pointer",
												}}
											/>
										</Box>
									)}
									{!!shift?.bonus && (
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<img src="/bonus.svg" alt="Bonus" style={{ marginTop: 4 }} />
											<Typography sx={{ color: "primary.dark", fontWeight: 600 }}>+${shift.bonus}</Typography>
										</Box>
									)}

									{shift?.shiftType && shift?.startTime && !readOnly && (
										<Box onClick={(e) => e.stopPropagation()}>
											<IconButton
												size="small"
												data-testid="shift-card-menu-button"
												onClick={(e) => handleMenuClick(e, shift.shiftType as string)}
												sx={{ ml: 1 }}
											>
												<MoreVertIcon sx={{ fontSize: 20, color: "primary.main" }} />
											</IconButton>
											<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
												<Box>
													{userCreatedOpenShift ? (
														<MenuItem
															disabled={isLoading}
															onClick={(e) => {
																handleCancelReplaceRequest(e, shift.shiftType as string);
															}}
														>
															{t("cancel.request")}
														</MenuItem>
													) : (
														<>
															{shift.startTime > DateTime.now() && (
																<>
																	{isReplacementRequestEnabled && (
																		<MenuItem data-testid="shift-card-replace" disabled={isLoading} onClick={(e) => handleReplaceShift(e, shift)}>
																			{t("replacement.request")}
																		</MenuItem>
																	)}
																	{isSwapEnabled && (
																		<MenuItem disabled={isLoading} onClick={(e) => handleSwapShift(e, shift)}>
																			{t("swap.request")}
																		</MenuItem>
																	)}
																</>
															)}
															{isBedSideCallOffEnabled &&
																(() => {
																	const now = DateTime.now().setZone(timezone);
																	const shiftDate = shift.startTime.setZone(timezone);
																	const hoursUntilShift = shiftDate.diff(now, "hours").hours;
																	const departmentData = userInfo.departments.find((d) => d.department_id === shift.departmentId);
																	const hoursSinceShiftStart = now.diff(shiftDate, "hours").hours;
																	const maxHoursBefore = departmentData?.callOffBypassHoursBeforeShiftBedside ?? 24;
																	const maxHoursAfter = departmentData?.callOffBypassHoursAfterStartBedside ?? 1;

																	const isDisabled = isLoading || hoursUntilShift > maxHoursBefore || hoursSinceShiftStart > maxHoursAfter;

																	return (
																		<MenuItem disabled={isDisabled} onClick={(e) => handleCallOffShift(e, shift)} sx={{ color: "#d32f2f" }}>
																			{t("call.out")}
																		</MenuItem>
																	);
																})()}
														</>
													)}
													{onCancelRequest && (
														<MenuItem
															disabled={isLoading}
															onClick={(e) => {
																e.stopPropagation();
																onCancelRequest();
															}}
														>
															{t("cancel.request")}
														</MenuItem>
													)}
												</Box>
											</Menu>
										</Box>
									)}
								</Box>
							</Box>
						)}
					</Box>
					{/* Aligns the status badge with the content column now that it sits below the row */}
					<Box sx={{ pl: `${STATUS_BADGE_INDENT_PX}px` }}>{dateStatus(dayRequests, shift)}</Box>
				</Paper>
			))}

			{variant === "full" && isExpanded && (hasOpenShifts || shifts?.some((s) => (s?.requests ?? 0) > 0)) && (
				<Box
					sx={{
						bgcolor: "#F8F9FB",
						borderRadius: "0 0 8px 8px",
						p: 2,
					}}
				>
					{(() => {
						const groupedRequests = groupRequests(dayRequests);

						// For each group, show pending if exists, otherwise show all
						return Object.values(groupedRequests).flatMap((group) => {
							// show only one status- if there is a pending, show pending, if there is an approved, show approved, if there is a declined or closed, show declined or closed
							const hasPending = group.some((r) => r.status === RequestResponsesStatusOptions.PENDING);
							const hasApproved = group.some((r) => r.status === RequestResponsesStatusOptions.APPROVED);
							const hasDeclined = group.some((r) => r.status === RequestResponsesStatusOptions.DECLINED);
							const hasClosed = group.some((r) => r.status === RequestResponsesStatusOptions.CLOSED);
							let requestsToShow = group;
							if (hasPending) {
								requestsToShow = group.filter((r) => r.status === RequestResponsesStatusOptions.PENDING);
							} else if (hasApproved) {
								requestsToShow = group.filter((r) => r.status === RequestResponsesStatusOptions.APPROVED);
							} else if (hasDeclined || hasClosed) {
								requestsToShow = group.filter((r) => r.status === RequestResponsesStatusOptions.DECLINED || r.status === RequestResponsesStatusOptions.CLOSED);
								// in case there were only closed or declined statuses, and a new open shift was created without the user to interact with
								// show the open shift as well so have the option to pick it
								if (group.some((r) => r.status === "")) {
									requestsToShow.push(...group.filter((r) => r.status === ""));
								}
							} else {
								requestsToShow = group;
							}
							return requestsToShow.map((openShift) => (
								<div key={openShift.id} style={{ marginBottom: "10px" }}>
									<OpenShiftCard nurse={nurse} shift={openShift} isExpanded={expandedShiftIds.includes(openShift.id)} onExpandClick={handleExpandShift} />
								</div>
							));
						});
					})()}
				</Box>
			)}
			<SwapDrawer weekIndex={selectedWeekIndex} open={selectedShiftForSwap !== null} onClose={() => setSelectedShiftForSwap(null)} shift={swapDrawerData} />
			<SystemDialog
				dataTestId="call-off-disciplinary-impact-dialog"
				open={shiftPendingCallOffConfirmation !== null}
				onClose={handleCancelCallOffImpact}
				title={t("call.out")}
				body={t("call.out.disciplinary.impact.body")}
				confirm={t("continue")}
				confirmButtonVariant="delete"
				onConfirm={handleConfirmCallOffImpact}
			/>
			{nurse && (
				<CallOffDrawer
					open={selectedShiftForCallOff !== null}
					onClose={() => setSelectedShiftForCallOff(null)}
					shift={selectedShiftForCallOff}
					nurseId={nurse.id}
				/>
			)}
			{selectedSwapRequest && (
				<SwapRequestDrawer
					open={selectedSwapRequest !== null}
					onClose={() => {
						setSelectedSwapRequest(null);
					}}
					shift={selectedSwapRequest}
				/>
			)}
		</Box>
	);
};
